import React from "react";
import { Sparkles, ShieldAlert, ArrowRightLeft, ListOrdered, Target, AlertTriangle, Trophy } from "lucide-react";
import RecommendationCard from "./RecommendationCard";
import CriteriaMatrix from "./CriteriaMatrix";

const VERDICT_TONE = {
  "Sign":                 { tone: "bg-emerald-600 text-white",       hint: "Conditions look acceptable" },
  "Sign with conditions": { tone: "bg-amber-500 text-white",         hint: "Address conditions before/at signing" },
  "Renegotiate":          { tone: "bg-orange-600 text-white",        hint: "Push back on key terms first" },
  "Do not sign":          { tone: "bg-red-600 text-white",           hint: "Material adverse risk" },
  "Accept":               { tone: "bg-emerald-600 text-white",       hint: "Top-ranked proposal looks acceptable" },
  "Accept with conditions": { tone: "bg-amber-500 text-white",       hint: "Accept top-ranked proposal with caveats" },
  "Decline all":          { tone: "bg-red-600 text-white",           hint: "No proposal is acceptable as-is" },
  "ranked":               { tone: "bg-zinc-900 text-white",          hint: "Prioritized action list" },
};

const SEVERITY_DOT = {
  Low: "bg-zinc-300", Medium: "bg-amber-400", High: "bg-rose-500", Critical: "bg-red-600",
};

function ExposureBar({ score }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const tone = pct >= 70 ? "bg-red-500" : pct >= 40 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="space-y-1.5" data-testid="exposure-bar">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">Exposure score</span>
        <span className="font-display tabular-nums text-2xl font-semibold text-zinc-950" data-testid="exposure-score-value">{pct}<span className="text-sm text-zinc-400">/100</span></span>
      </div>
      <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ScenarioBlock({ title, scenario, tone, testId }) {
  const newRisks = scenario?.new_risks || [];
  const newObligations = scenario?.new_obligations || [];
  const removedProtections = scenario?.removed_protections || [];
  const empty = newRisks.length + newObligations.length + removedProtections.length === 0;
  return (
    <section
      data-testid={testId}
      className={`rounded-xl border ${tone} p-4 space-y-3 min-h-[160px]`}
    >
      <h4 className="text-xs uppercase tracking-[0.18em] font-semibold">{title}</h4>
      {empty && <p className="text-xs italic opacity-70">No material change identified.</p>}
      {newRisks.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider opacity-80 font-medium mb-1">New risks</p>
          <ul className="space-y-1">
            {newRisks.map((r, i) => (
              <li key={i} className="text-sm leading-snug flex gap-1.5">
                <span>•</span><span>{typeof r === "string" ? r : (r.text || JSON.stringify(r))}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {newObligations.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider opacity-80 font-medium mb-1">New obligations</p>
          <ul className="space-y-1">
            {newObligations.map((o, i) => (
              <li key={i} className="text-sm leading-snug flex gap-1.5">
                <span>•</span><span>{typeof o === "string" ? o : (o.what || o.text || JSON.stringify(o))}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {removedProtections.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider opacity-80 font-medium mb-1">Lost protections</p>
          <ul className="space-y-1">
            {removedProtections.map((p, i) => (
              <li key={i} className="text-sm leading-snug flex gap-1.5">
                <span>•</span><span>{typeof p === "string" ? p : (p.text || JSON.stringify(p))}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Generic decision viewer — renders ANY decision_v1 record.
 * Adapts to mode by switching between scenarios (simulation) and
 * ranked_subjects (ranking) sections.
 */
export default function DecisionPanel({ decision, subjectsById = {} }) {
  if (!decision) return null;
  const verdictMeta = VERDICT_TONE[decision.verdict] || VERDICT_TONE.ranked;
  const isSimulation = !!decision.scenarios;
  const hasRanked = Array.isArray(decision.ranked_subjects) && decision.ranked_subjects.length > 0;
  const isCompare = decision.mode === "compare_proposals" && !!decision.criteria_matrix;
  const recs = decision.recommendations || [];
  const topRisks = decision.top_risks || [];
  const winnerId = isCompare ? decision.ranked_subjects?.[0]?.doc_id : null;

  return (
    <div className="space-y-6" data-testid="decision-panel">
      {/* Verdict header */}
      <header
        data-testid="decision-verdict-header"
        className="rounded-2xl border border-zinc-200 bg-gradient-to-br from-white via-zinc-50 to-white p-5 sm:p-6"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles size={13} className="text-zinc-500" strokeWidth={1.75} />
              <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
                {decision.mode === "should_i_sign" ? "Sign decision"
                  : decision.mode === "prioritize_actions" ? "Action priorities"
                  : decision.mode === "compare_proposals" ? "Proposal comparison"
                  : "Decision"}
              </p>
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950" data-testid="decision-question">
              {decision.question}
            </h2>
            <p className="text-xs text-zinc-500 mt-1.5">
              Confidence: <span className="font-semibold text-zinc-700" data-testid="decision-confidence">{Math.round((decision.confidence || 0) * 100)}%</span>
              {" · "}Model: <span className="font-mono">{decision.model}</span>
            </p>
          </div>
          <div className="text-right">
            <span
              data-testid="decision-verdict-pill"
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full font-semibold tracking-wide text-sm ${verdictMeta.tone}`}
            >
              {decision.verdict}
            </span>
            <p className="text-[11px] text-zinc-500 mt-1.5">{verdictMeta.hint}</p>
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-zinc-200 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <ExposureBar score={decision.exposure_score} />
          {decision.biggest_exposure_source && (
            <div data-testid="biggest-exposure-source">
              <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium mb-1">Biggest exposure source</p>
              <p className="font-mono text-xs bg-zinc-100 px-2 py-1 rounded inline-block">{decision.biggest_exposure_source}</p>
            </div>
          )}
        </div>
      </header>

      {/* Scenarios (simulation modes only) */}
      {isSimulation && (
        <section data-testid="decision-scenarios">
          <div className="flex items-center gap-2 mb-3">
            <ArrowRightLeft size={14} className="text-zinc-700" strokeWidth={1.75} />
            <h3 className="font-display font-semibold text-base tracking-tight">Current vs If you sign</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ScenarioBlock
              title="If you sign"
              scenario={decision.scenarios?.selected}
              tone="bg-rose-50 border-rose-200 text-rose-900"
              testId="scenario-selected"
            />
            <ScenarioBlock
              title="If you do NOT sign"
              scenario={decision.scenarios?.alternative}
              tone="bg-emerald-50 border-emerald-200 text-emerald-900"
              testId="scenario-alternative"
            />
          </div>
        </section>
      )}

      {/* Winner banner (compare_proposals only — 2c: verdict pill above ranked list) */}
      {isCompare && winnerId && (
        <section
          data-testid="decision-winner-banner"
          className="rounded-xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-4 flex items-center gap-3"
        >
          <Trophy size={20} className="text-emerald-600 shrink-0" strokeWidth={1.75} />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">
              Recommended choice · Verdict applies to this proposal
            </p>
            <p className="font-display font-semibold text-zinc-950 truncate" data-testid="decision-winner-name">
              {subjectsById[winnerId]?.filename || winnerId.slice(0, 8)}
            </p>
          </div>
          <span className={`px-3 py-1.5 rounded-full font-semibold text-xs ${verdictMeta.tone}`}>
            {decision.verdict}
          </span>
        </section>
      )}

      {/* Ranked subjects (ranking modes only) */}
      {hasRanked && (
        <section data-testid="decision-ranked-subjects">
          <div className="flex items-center gap-2 mb-3">
            <ListOrdered size={14} className="text-zinc-700" strokeWidth={1.75} />
            <h3 className="font-display font-semibold text-base tracking-tight">
              {isCompare ? "Proposal ranking" : "Prioritized action areas"}
            </h3>
          </div>
          <ol className="space-y-2">
            {decision.ranked_subjects.map((r, i) => (
              <li
                key={i}
                data-testid={`ranked-subject-${i}`}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                  r.rank === 1 && isCompare
                    ? "border-emerald-300 bg-emerald-50/40"
                    : "border-zinc-200 bg-white hover:bg-zinc-50"
                }`}
              >
                <span className="font-display font-semibold tabular-nums text-zinc-400 w-6 shrink-0" data-testid={`ranked-subject-rank-${i}`}>#{r.rank}</span>
                <div className="min-w-0 flex-1">
                  {isCompare && r.doc_id && subjectsById[r.doc_id] && (
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-0.5">
                      {subjectsById[r.doc_id].filename}
                    </p>
                  )}
                  <p className="text-sm font-semibold text-zinc-950 leading-snug" data-testid={`ranked-subject-headline-${i}`}>{r.headline}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-[11px] text-zinc-500">
                    <Target size={11} strokeWidth={1.75} />
                    <span>Exposure: <span className="font-semibold text-zinc-700 tabular-nums">{r.exposure_score}</span></span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Criteria matrix (compare_proposals only) */}
      {isCompare && (
        <CriteriaMatrix
          matrix={decision.criteria_matrix}
          subjectsById={subjectsById}
          trade_offs={decision.trade_offs || []}
        />
      )}

      {/* Top risks */}
      {topRisks.length > 0 && (
        <section data-testid="decision-top-risks">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert size={14} className="text-zinc-700" strokeWidth={1.75} />
            <h3 className="font-display font-semibold text-base tracking-tight">Top risks</h3>
          </div>
          <ul className="space-y-2">
            {topRisks.map((r, i) => (
              <li
                key={i}
                data-testid={`decision-risk-${i}`}
                className="flex items-start gap-3 p-3 rounded-lg border border-zinc-200 bg-white"
              >
                <AlertTriangle size={13} className="text-zinc-500 shrink-0 mt-0.5" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-800 leading-relaxed">{r.text}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${SEVERITY_DOT[r.severity] || SEVERITY_DOT.Medium}`} />
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">{r.severity}</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400">· {r.source_kind}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Recommendations */}
      <section data-testid="decision-recommendations">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-zinc-700" strokeWidth={1.75} />
          <h3 className="font-display font-semibold text-base tracking-tight">
            Recommended next actions
            <span className="text-xs text-zinc-500 font-normal ml-2">({recs.length})</span>
          </h3>
        </div>
        {recs.length === 0 ? (
          <p className="text-sm text-zinc-500 italic">No actionable recommendations generated.</p>
        ) : (
          <div className="space-y-3">
            {recs.map((rec, i) => (
              <RecommendationCard key={i} rec={rec} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
